import PropTypes from 'prop-types';
import React, { Component, forwardRef, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as dataEntity from 'graphql/DataEntity';
import { serviceActions } from 'state/modules/service';
import { viewActions } from 'state/modules/view';
import * as viewHelper from 'utils/ViewHelper';
import { createDefaultViewConfig } from 'utils/ViewHelper';
import { forceReload } from 'components/connectedForm/utils/actions';
import EditableDataGrid from 'components/EditableDataGrid';
import DetailDataGrid from 'components/DetailDataGrid';
import ViewToolbar from '../../components/view/ViewToolbar';
import FiltersContainer from '../../components/view/FiltersContainer';
import ViewController from 'components/view/ViewController';
import btnStyles from 'styles/partials/_button.css';
import componentStyles from 'styles/partials/_workspace.css';
import * as FormUtils from 'utils/FormHelper';
import Formatters from 'components/grid-addons/formatters';
import { getModuleName } from 'utils/getModuleName';
import checkReadOnly from 'components/connectedForm/utils/checkReadOnly';
import getIn from 'components/connectedForm/utils/getIn';
import styled from 'styled-components';
import { MenuItem } from 'react-contextmenu';
import setDocumentTitle from 'utils/SetDocumentTitle';
import Operations from 'utils/operations';
import { ALLOW_ADD, ALLOW_DELETE, ALLOW_EDIT, ALLOW_VIEW, checkOperation } from '../../utils/OperationPermissions';
import { formLinkHandleClick } from '../../utils/LinkUtils';
import { generateCustomBreadcrumb } from 'components/connectedForm/BreadCrumbs';
import { InfoButton, PrimaryButton, SecondaryButton } from '../connectedForm/Button';
import AppContext from '../Context/AppContext';
import { useDispatch, useSelector } from 'react-redux';
import WorkspaceContext from '../Context/WorkspaceContext';
import FluentList from '../FluentWrapper/FluentList';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import { FilterListsView } from './FilterList';
import { ViewContextProvider } from '../Context/ViewContext';
import ModalContext from '../Context/ModalContext';
import { variableDebounce } from '../../utils/DebounceUtil';
import { Analytics } from 'mixpanel';
import { WorkspaceActionButtons } from './WorkspaceActionButtons';
import { HiddenVerticalScrollMenu, ItemRenderer } from '../connectedForm/ActionButtons';
import { AlertType, Direction } from '../../graphql/schema/p4types';
import { TotalsView } from './TotalsView';
import { useCompany } from '../../utils/FetchConfig';
import { useWorkspaceSelection } from './useWorkspaceSelection';
import setIn from '../connectedForm/utils/setIn';
import { useHistory } from 'react-router';
import BetaTag from '../BetaTag';
import WorkspaceOverviewBar from './WorkspaceOverviewBar';
import ModalContentLink from './ModalContentLink';
import GridToolbarWorkspaceSearch from '../GridToolbar/GridToolbarWorkspaceSearch';
import GridToolbarContainer from '../GridToolbar/GridToolbarContainer';
import SidebarContext, { SidebarType } from '../Context/SidebarContext';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import { makeStyles } from '@fluentui/react-components';

const InfoBannerOuter = styled.div`
  border-bottom: 1px solid #e0e0e0;
  border-left-width: 3px;
  border-left-style: solid;
  border-left-color: #ffa000;
`;

const InfoBanner = styled.div`
  padding: 10px 14px;
  margin: 0px 12px 0px 0px;
  position: relative;
`;

const InfoBannerAction = styled.div`
  position: absolute;
  right: 10px;
  top: 3px;
`;

export const calcTableHeight = (props, { viewToolbarRef, state }) => {
  if (props.contentHeight) return props.contentHeight;
  const reduceHeight = props.embeddedView ? 65 : 0;
  let viewHeight = 70;
  if (viewToolbarRef.current?.clientHeight > 0) {
    viewHeight = viewToolbarRef.current.clientHeight + 24;
  }
  if (props.listData?.totalColumns?.length > 0 || props.additionalCards) {
    viewHeight += 75;
  }
  if (state) {
    //Needs to be skipped when state is not set because this function is called by the constructor to initialize state in the first place.
    const hasListFilters = (props.currentView?.filterAttributeObjects || []).some((filterAttributeObject) => {
      return filterAttributeObject?.filterStyle === 'LIST';
    });
    if (!props.currentView?.hideConfiguration && hasListFilters) {
      viewHeight += window.innerHeight < 800 ? 212 : 312;
    }
  }

  return window.innerHeight - 145 - viewHeight - reduceHeight - (props.childrenHeight ?? 0);
};

//If the current workspace is nested inside a modal, adds an action button to trigger closing that modal.
const CloseModalActionButton = () => {
  const { onClose: closeWrappingModal } = useContext(ModalContext);

  return closeWrappingModal ? <InfoButton onClick={closeWrappingModal}>Close</InfoButton> : null;
};

const getWorkspaceContextButtons = ({
  enableRowSelect, //state
  permittedOperations, //props
  noEdit, //props
  formUrl, //props
  onEditClick, //
  formIdOverride, //props
  duplicate, //props
  duplicateData, //
  handleActionSort, //
  renderActionButtons, //props
  columnOverrides,
}) => {
  return ({ rowData, columnIdx, currentView, screenReference, entity, app }) => {
    const renderedButtons = [];

    //Need to bump the column index when row select is enabled, otherwise we'll sort an offset column.
    const column = currentView?.columns?.[columnIdx - (enableRowSelect ? 1 : 0)];
    if (!column) return null;
    const fieldName = column.fieldName;

    const updatePermitted = checkOperation({ screenReference, entity, app }, Operations.Update);
    const rowActionTitle = updatePermitted && permittedOperations & ALLOW_EDIT ? 'Edit' : 'View';
    if (rowData.id && !noEdit && formUrl) {
      renderedButtons.push(
        <MenuItem
          key={'actionButtonEdit'}
          onClick={(e) => {
            e.preventDefault();
            onEditClick(getIn(rowData, formIdOverride || 'id'));
          }}
        >
          {rowActionTitle}
        </MenuItem>
      );
      renderedButtons.push(
        <MenuItem
          key={'actionButtonEditNewTab'}
          onClick={(e) => {
            e.preventDefault();
            onEditClick(getIn(rowData, formIdOverride || 'id'), undefined, true);
          }}
        >
          {rowActionTitle} (New tab)
        </MenuItem>
      );
    }

    const createPermitted = checkOperation({ screenReference, entity, app }, Operations.Create);
    if (createPermitted && duplicate === true) {
      renderedButtons.push(
        <MenuItem
          key={'actionButtonDuplicate'}
          onClick={(e) => {
            e.preventDefault();
            duplicateData(rowData.id);
          }}
        >
          Duplicate
        </MenuItem>
      );
    }

    if (columnOverrides?.[column.fieldName]?.sortable ?? true) {
      renderedButtons.push(
        <MenuItem
          key={'actionButtonSortAsc'}
          onClick={(e) => {
            e.preventDefault();
            handleActionSort(fieldName, Direction.Asc);
          }}
        >
          Sort Ascending
        </MenuItem>
      );
      renderedButtons.push(
        <MenuItem
          key={'actionButtonSortDesc'}
          onClick={(e) => {
            e.preventDefault();
            handleActionSort(fieldName, Direction.Desc);
          }}
        >
          Sort Descending
        </MenuItem>
      );
    }

    if (renderActionButtons) {
      renderedButtons.push(...renderActionButtons({ rowData, columnIdx }));
    }

    return renderedButtons;
  };
};

class Workspace extends Component {
  constructor(props) {
    super(props);

    this.gridRowHeight = props.miniGrid ? 25 : 36;
    this.listRowHeight = 50;
    this.sortBy = props.initialSortColumn;
    this.sortDir = props.initialSortDir;
    this.messageDefinition = null;
    this.sbMinWidth = 240;
    this.queue = [];
    this.dataStoreId = props.entityType + dataEntity.getUniqueStore();
    this.pathname = null;
    this.styles = {};
    Object.assign(this.styles, componentStyles, btnStyles);

    this.onRowClick = this.onRowClick.bind(this);
    this.onRowDoubleClick = this.onRowDoubleClick.bind(this);
    this.update = this.update.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onSwitchView = this.onSwitchView.bind(this);
    this._nextRecord = this._nextRecord.bind(this);
    this._prevRecord = this._prevRecord.bind(this);
    this.queueRequestFor = this.queueRequestFor.bind(this);
    this.queueRequestDebounced = variableDebounce(this.queueRequestDebounced.bind(this));
    this.loadMoreRows = this.loadMoreRows.bind(this);
    this.handleGridSort = this.handleGridSort.bind(this);
    this.handleColumnReorder = this.handleColumnReorder.bind(this);
    this.autoFocusEl = this.autoFocusEl.bind(this);
    this._tableView = this._tableView.bind(this);
    this.getAllTablesMessageDefinition = this.getAllTablesMessageDefinition.bind(this);
    this.renderViewController = this.renderViewController.bind(this);
    this.renderViewToolbar = this.renderViewToolbar.bind(this);
    this.renderForm = this.renderForm.bind(this);
    this.getRowNo = this.getRowNo.bind(this);
    this.renderAddButton = this.renderAddButton.bind(this);
    this.renderAddOptions = this.renderAddOptions.bind(this);
    this.handleActionOption = this.handleActionOption.bind(this);
    this.handleActionSort = this.handleActionSort.bind(this);
    this.handleActionFilter = this.handleActionFilter.bind(this);
    this.handleActionHide = this.handleActionHide.bind(this);
    this.setApiCode = this.setApiCode.bind(this);
    this.checkGraphQLErrors = this.checkGraphQLErrors.bind(this);
    this.duplicateData = this.duplicateData.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onModalSelect = this.onModalSelect.bind(this);
    this.backToMenu = this.backToMenu.bind(this);
    this.onDeleteClick = this.onDeleteClick.bind(this);
    this.getRowCounts = this.getRowCounts.bind(this);
    window.addEventListener('resize', this.onResize, false);

    this.view = {
      columns: {},
      hideConfiguration: true,
      name: 'default',
      orderBy: [
        {
          fieldName: 'warehouse.description',
          isDescending: false,
        },
      ],
    };

    this.actionContextButtonRef = React.createRef();
    this.workspaceContainerRef = React.createRef();
    this.tableViewRef = React.createRef();
    this.viewToolbarRef = React.createRef();

    this.state = {
      detailRecord: null,
      contentWidth: props.contentWidth ? props.contentWidth : window.innerWidth - 300,
      contentHeight: this._calcTableHeight(props),
      tableName: 'grid',
      sbVisible: false,
      sbWidth: 240,
      recordNo: 0,
      reportModalActive: false,
      searchValue: '',
      searchIndex: -1,
      views: null,
      viewScreenName: props.viewScreenName,
    };
  }

  checkGraphQLErrors(nextProps) {
    const prevErrors = this.props.error && this.props.error.graphQLErrors;
    const errors = nextProps.error && nextProps.error.graphQLErrors;
    if (errors && errors.length > 0 && (!prevErrors || (prevErrors && prevErrors.length === 0))) {
      this.props.dispatch(
        serviceActions.genericAlert(`${this.props.title}`, `Error fetching ${this.props.title} data`)
      );
    }
  }

  componentDidMount() {
    const { modtitle } = getModuleName(this.props.location);
    if (!this.props.embeddedView && !this.props.miniGrid) setDocumentTitle(`${this.props.title} - ${modtitle}`);
    this.prevLocation = this.context.location;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    this.updateTableNameIfUrlHistoryUpdated(prevProps);
    if (this.props.refreshRequired) this.forceRefresh();

    if (this.props.error) this.checkGraphQLErrors(this.props);

    this.recalculateTableDimensions(prevState);

    this.workspaceContainerRef.current.style.display = 'block';

    const nextLocation = this.context.location;
    const prevLocation = this.prevLocation;
    this.prevLocation = nextLocation;

    let nextPath = nextLocation.pathname.slice(1).toLowerCase();
    if (nextPath.endsWith('/')) nextPath = nextPath.slice(0, -1);
    let thisPath = prevLocation.pathname.slice(1).toLowerCase();
    if (thisPath.endsWith('/')) thisPath = thisPath.slice(0, -1);
    const viewScreenName = this.props.viewScreenName.toLowerCase();
    const nextAction = nextLocation.action;
    const thisAction = prevLocation.action;

    // If returning to this screen i.e. from the form for adding an object which is in a modal reload data as records
    // added/edited/deleted will need to be updated in grid
    if (
      (nextPath !== thisPath && nextPath === viewScreenName) ||
      (nextAction === 'REPLACE' && nextAction !== thisAction) ||
      this.refreshRequired
    ) {
      const rowNo = this.getRowNo();
      this.loadMoreRows(rowNo + this.props.pageSize, true);
    }
    this.refreshRequired = false;
  }

  componentWillUnmount() {
    if (this.props.app.isLoggedIn && this.props.views?.[this.props.viewScreenName]) {
      this.props.dispatch(viewActions.switchView(this.props.viewScreenName, null));
    }
    window.removeEventListener('resize', this.onResize);
  }

  getRowNo() {
    if (this.state.tableName === 'card') return 0;
    const rowHeight = this.state.tableName === 'form' ? this.listRowHeight : this.gridRowHeight;
    const gridCanvas = document.getElementsByClassName('react-grid-Canvas')[0];

    // If there are no results in grid there will be no gridCanvas so use row 0
    if (!gridCanvas) {
      return 0;
    }

    const firstRow = Math.floor(gridCanvas.scrollTop / rowHeight);
    return firstRow - (firstRow % this.props.pageSize);
  }

  setApiCode(code) {
    this.props.dispatch(serviceActions.setApiCode(this.dataStoreId, code));
  }

  queueRequestFor(rowIndex) {
    this.queueRequestDebounced(250, rowIndex);
  }

  queueRequestDebounced(rowIndex) {
    this.queue.push(rowIndex);
    if (!this.queueFlushID) {
      this.queueFlushID = setTimeout(this._flushRequestQueue.bind(this), 0);
    }
  }

  _flushRequestQueue() {
    const sectionsToLoad = this.queue.reduceRight((requestSections, rowIndex) => {
      const rowBase = rowIndex - (rowIndex % this.props.pageSize);
      if (requestSections.indexOf(rowBase) === -1) {
        return requestSections.concat(rowBase);
      }
      return requestSections;
    }, []);
    sectionsToLoad.forEach((rowBase) => {
      this.loadMoreRows(rowBase + this.props.pageSize);
    });
    this.queue = [];
    this.queueFlushID = null;
  }

  loadMoreRows(rowEnd, replaceData) {
    if (replaceData) {
      this.queue = [];
    }
    this.props.loadMoreRows(rowEnd - this.props.pageSize, replaceData);
  }

  handleGridSort(sortColumn, sortDirection) {
    let sortBy, sortDescending;
    if (sortDirection !== 'NONE') {
      sortBy = sortColumn;
      sortDescending = sortDirection === Direction.Desc;
    } else {
      sortBy = this.props.initialSortColumn;
      sortDescending = this.props.initialSortDir === Direction.Desc;
    }
    this.props.dispatch(viewActions.updateSort(this.props.viewScreenName, sortBy, sortDescending));
  }

  handleColumnReorder(columns) {
    let position = 0;

    let updatedColumns = columns.map((column) => {
      const columnDefinition = this.props.availableColumns.find((availableColumn) => {
        return availableColumn.fieldName === (column.fieldName || column.key);
      });

      return Object.assign({}, columnDefinition, {
        width: column.width,
        position: position++,
        name: column.title || column.name,
        key: column.fieldName || column.key,
      });
    });

    // Filter columns without key (edit column)
    updatedColumns = updatedColumns.filter((column) => column.key);

    this.props.dispatch(viewActions.updateColumns(this.props.viewScreenName, updatedColumns));
  }

  onRowClick(idx, row, column) {
    if (idx === -1) return;

    const rowData = this.props.listData.edges[idx].node;
    if (this.props.onRowClick) this.props.onRowClick(rowData, idx, column);
    const sbVisible = false;
    if (this.props.mode === 'full' && this.props.detailView) {
      const id = this.state.detailRecord && this.state.detailRecord.id;
      if (id && id !== rowData.id) this.props.dispatch(forceReload(this.props.entityType));
      this.setState({
        sbVisible: sbVisible,
        detailRecord: rowData,
        contentWidth: this._calcTableWidth(this.props.app.appMenuHidden, sbVisible, this.state.sbWidth),
      });
    }
  }

  onRowDoubleClick(idx, rowData) {
    if (this.props.onRowDoubleClickOverride) {
      this.props.onRowDoubleClickOverride(rowData, idx);
      return;
    }

    const readOnly = checkReadOnly(this.props.readOnlyStatus, rowData);

    //Build up dependentValues to match the structure we have in 'handleClick' in LinkFormatter.tsx
    const dependentValues = {
      rowData,
      options: {
        url: this.props.getFormUrl(rowData),
        entity: { type: this.props.entityType || this.props.entity?.type },
        editInNewTab: this.props.editInNewTab,
      },
    };

    if (!this.props.noEdit) {
      // If the current sidebar context is within a modal and this is a supported type, open the sidebar instead of navigating.
      if (this.props.sidebarContext.modal) {
        switch (this.props.entity.type) {
          case 'Product':
            this.props.sidebarContext.openSidebar(SidebarType.ProductForm, rowData.id);
            return;
          case 'Supplier':
            this.props.sidebarContext.openSidebar(SidebarType.SupplierForm, rowData.id);
            return;
          case 'Customer':
            this.props.sidebarContext.openSidebar(SidebarType.CustomerForm, rowData.id);
            return;
        }
      }

      formLinkHandleClick(
        { dispatch: this.props.dispatch, history: this.context.history, formActiveTabId: this.props.formActiveTabId },
        dependentValues,
        getIn(rowData, this.props.formIdOverride || 'id'),
        readOnly
      );
    }
  }

  onSwitchView(tableName) {
    const sbVisible = tableName === 'form' ? false : this.state.sbVisible;
    this.setState({
      contentWidth: this._calcTableWidth(this.props.app.appMenuHidden, sbVisible, this.state.sbWidth),
      tableName: tableName,
      sbVisible: sbVisible,
    });
  }

  _nextRecord() {
    const recordNo =
      this.state.recordNo < this.props.listData.edges.length - 1 ? this.state.recordNo + 1 : this.state.recordNo;
    this.setState({
      recordNo: recordNo,
    });
  }

  _prevRecord() {
    const recordNo = this.state.recordNo ? this.state.recordNo - 1 : 0;
    this.setState({
      recordNo: recordNo,
    });
  }

  autoFocusEl(el) {
    if (el !== null) {
      el.focus();
    }
  }

  onSubmit() {
    if (this.props.embeddedView) {
      // put something here
    } else {
      this.context.history.push({
        pathname: `${this.props.getFormUrl()}New`,
        state: {
          modal: true,
          popover: false,
          previousLocation: this.context.history.location?.pathname || 'back',
          defaultValues: this.props.addDefaultValues || null,
        },
      });
    }
  }

  backToMenu(path) {
    this.context.history.push({
      pathname: path,
      state: { previousLocation: this.context.history.location?.pathname || 'back' },
    });
  }

  closePopOut = () => {
    if (this.actionContextButtonRef.current) {
      this.actionContextButtonRef.current.popoutClose();
    }
  };

  renderAddButton() {
    const tabEnable = this.props.tabIndex === -1 ? false : this.props.focusArea === 'Work';

    return (
      <div className={this.styles.hdrLink}>
        <PrimaryButton
          data-testid="wsAdd"
          onClick={this.onSubmit}
          tabIndex={tabEnable ? 1 : -1}
          text={this.props.addButtonLabel}
          styles={{
            root: {
              width: '150px',
            },
          }}
        />
      </div>
    );
  }

  renderAddOptions() {
    const formUrl = this.props.formUrl ?? this.props.entity?.formUrl;
    if (this.props.noAddOption || !formUrl || !(this.props.permittedOperations & ALLOW_ADD)) return null;
    if (!checkOperation(this.props, Operations.Create)) return null;

    if (this.props.addButtons.length === 0) {
      return this.renderAddButton();
    }

    const renderedButtons = this.props.addButtons.map((addButton, idx) => {
      return {
        key: `wsAdd-${addButton.description.replace(' ', '')}`,
        text: addButton.description,
        onClick: () => {
          this.context.history.push({
            pathname: addButton.url,
            state: {
              modal: true,
              popover: false,
              previousLocation: this.context.history.location?.pathname || 'back',
              defaultValues: this.props.addDefaultValues || null,
            },
          });
        },
      };
    });

    const tabEnable = this.props.tabIndex === -1 ? false : this.props.focusArea === 'Work';

    return (
      <PrimaryButton
        text={`Add ${this.props.title}`}
        menuAs={HiddenVerticalScrollMenu}
        menuProps={{
          contextualMenuItemAs: ItemRenderer,
          items: renderedButtons,
        }}
        persistMenu
        tabIndex={tabEnable ? 1 : -1}
        style={{ order: 3 }}
        primary={true}
      />
    );
  }

  onModalSelect(data) {
    if (this.props.onModalSelect) {
      this.props.onModalSelect(data);
    } else {
      this.props.onRowClick(data);
    }
  }

  duplicateData(id) {
    this.context.history.push({
      pathname: `${this.props.getFormUrl()}New`,
      state: {
        modal: true,
        popover: false,
        duplicateId: id,
        previousLocation: this.context.history.location?.pathname || 'back',
      },
    });
  }

  handleActionHide(idx) {
    const selectedColumns = this.props.currentView.tables[0].columns;
    selectedColumns.splice(idx, 1);
    this.props.dispatch(viewActions.updateColumns(this.props.viewScreenName, selectedColumns));
  }

  handleActionSort(columnName, sortDir) {
    this.handleGridSort(columnName, sortDir);
  }

  handleActionFilter(fieldName, fieldValue, fieldType) {
    const filterType = viewHelper.FILTER_TYPE_MAP[fieldType] || '';
    const filter = {
      currentView: this.props.currentView,
      fieldName: fieldName,
      type: filterType,
    };
    if (fieldType !== 'Boolean') {
      filter.containsText = fieldValue;
      filter.from = fieldValue;
      filter.to = fieldValue;
    } else if (fieldValue) {
      filter.Yes = true;
      filter.No = false;
    } else {
      filter.Yes = false;
      filter.No = true;
    }
    const addFilters = [filter];
    const removeFilters = [];

    this.props
      .dispatch(() =>
        Promise.all([
          this.props.dispatch(viewActions.updateFilters(this.props.viewScreenName, addFilters, removeFilters)),
        ])
      )
      .then(() => {
        this.props.dispatch(viewActions.updateFilter(this.props.viewScreenName, filter));
      });
  }

  handleActionOption(option) {
    Analytics.track('Workspace action selected', { label: option.label });

    const ignoreState = option.ignoreState;
    const selectionState = !ignoreState ? this.props.workspaceSelection.getSelectionState() : null;

    if (option.url) {
      this.context.history.push({
        pathname: option.url,
        state: {
          ...selectionState,
          previousLocation: this.context.history.location?.pathname || 'back',
        },
      });
    }

    if (!ignoreState && selectionState && selectionState.count > 0) {
      this.props.dispatch(
        serviceActions.genericAlert(
          option.alert && option.alert.title ? option.alert.title : option.description,
          option.alert && option.alert.message
            ? option.alert.message(selectionState.count)
            : `Preparing ${selectionState.count} records.`,
          AlertType.Info
        )
      );
    }

    const metaData = {
      dataStoreId: this.dataStoreId,
      forceRefresh: this.forceRefresh,
    };

    if (option.dispatch) {
      this.props.dispatch(option.dispatch(this.context, metaData));
    }
    if (option.call) {
      option.call({
        ...this.props,
        reloadData: this.reloadData,
        location: {
          state: {
            selectedAll: !this.props.workspaceSelection.enableRowSelect || this.props.workspaceSelection.selectedAll,
          },
        },
      });
    }
  }

  forceRefresh = () => {
    this.refreshRequired = true;
    this.forceUpdate();
  };

  renderViewController(tabEnable, allowViewUpdates) {
    if (!this.props.views?.[this.props.viewScreenName]) {
      return null;
    }

    return (
      <ViewController
        user={this.props.app.user}
        viewUpdate={this.props.viewUpdate}
        embeddedView={this.props.embeddedView}
        refreshViews={this.props.storedViews.refreshViews}
        tabIndex={tabEnable ? 1 : -1}
        allowViewUpdates={allowViewUpdates}
      />
    );
  }

  getRowCounts() {
    const rowCount = this.props.listData?.count ? this.props.listData.count : 0;
    let selectedRowCount;
    if (this.props.workspaceSelection.enableRowSelect) {
      selectedRowCount = this.props.workspaceSelection.selectedAll
        ? rowCount - this.props.workspaceSelection.selectedRows.length
        : this.props.workspaceSelection.selectedRows.length;
    } else {
      selectedRowCount = rowCount;
    }

    let genericCount;
    if (this.props.listData?.genericCount >= 0) {
      //If genericCount is returned - Accurate representation of how many results we wish to display, use as the rowCount.
      genericCount = this.props.listData.genericCount;
    } else if (this.props.listData?.pageInfo?.hasNextPage) {
      genericCount = this.props.listData.edges.length + 1;
    }

    return { rowCount, selectedRowCount, genericCount };
  }

  renderViewToolbar(tabEnable, viewController) {
    if (!this.props.views?.[this.props.viewScreenName]) {
      return null;
    }

    const { rowCount, selectedRowCount } = this.getRowCounts();

    return (
      <div ref={this.viewToolbarRef}>
        <ViewToolbar
          onSwitchView={this.onSwitchView}
          tabEnable={tabEnable}
          tableName={this.state.tableName}
          detailView={!!this.props.detailView}
          viewController={viewController}
          embeddedView={this.props.embeddedView}
          backButtonRequired={this.props.backButtonRequired}
          onBack={this.props.onBack}
          hideColumnSelector={this.props.hideColumnSelector}
          hideViewController={this.props.hideViewController}
          count={rowCount}
          selectedCount={selectedRowCount}
          includeGenericSearch={this.props.includeGenericSearch}
          data-testid={this.props.testId}
          customToolbarAction={this.props.customToolbarAction}
          onResetClick={this.props.onResetClick}
          preventSelectionResetOnFilterChange={this.props.preventSelectionResetOnFilterChange}
        />
      </div>
    );
  }

  onResize() {
    this.update();
  }

  update() {
    this.setState({
      contentWidth: this._calcTableWidth(this.props.app.appMenuHidden, this.state.sbVisible),
      contentHeight: this._calcTableHeight(this.props),
    });
  }

  recalculateTableDimensions() {
    const newTableWidth = this._calcTableWidth(this.props.app.appMenuHidden, this.state.sbVisible, this.state.sbWidth);
    const newTableHeight = this._calcTableHeight(this.props);

    if (newTableHeight !== this.state.contentHeight || newTableWidth !== this.state.contentWidth) {
      this.setState({
        contentWidth: newTableWidth,
        contentHeight: newTableHeight,
      });
    }
  }

  _calcTableWidth(menuHidden, sbVisible, sbWidth) {
    if (this.props.contentWidth) return this.props.contentWidth;
    let menuAdj = menuHidden ? 0 : 206;
    if (!this.props.app.appSidebarHidden) menuAdj += 300;
    const sbAdj = sbVisible ? sbWidth : 0;
    const lookupAdj = this.props.lookupView ? window.innerWidth * 0.2 - menuAdj - 60 : 0;
    const embed = this.props.embeddedView ? 16 : 0;
    return this.props.pageView
      ? window.innerWidth * 0.85 - 30
      : window.innerWidth - 84 - menuAdj - sbAdj - lookupAdj + embed;
  }

  _calcTableHeight(props) {
    return (props.calcTableHeight ?? calcTableHeight)(props, {
      viewToolbarRef: this.viewToolbarRef,
      currentView: this.props.currentView,
      state: this.state,
    });
  }

  // Setup message definition to satisfy all columns used by all tables
  // Required message definition is returned as an object with each required path as a property of the object
  getAllTablesMessageDefinition() {
    if (!this.props.views?.[this.props.viewScreenName]) {
      return null;
    }

    const md = this.props.messageDefinition || {};

    this.props.currentView.tables.forEach((table) => {
      table.columns.forEach((column) => {
        FormUtils.addFieldToMessageDefinition(md, column.fieldName);
        if (column.formatter === 'AttributeFormatter') {
          const keys = column.fieldName.split('.');
          keys.pop();
          const keyPath = keys.length === 0 ? '' : `${keys.join('.')}.`;
          FormUtils.addFieldToMessageDefinition(md, `${keyPath}attribute.attributeReferenceList.*`);
        }
      });
    });

    return md;
  }

  renderForm() {
    let id = 'New';
    if (this.state.detailRecord) {
      id = getIn(this.state.detailRecord, this.props.formIdOverride || 'id');
    } else if (this.props.listData?.edges?.[0]) {
      id = getIn(this.props.listData.edges[0].node, this.props.formIdOverride || 'id');
    }
    if (id === 'New') {
      return null;
    }
    const DetailView = this.props.detailView;

    return (
      <ViewContextProvider value={{ uniqueName: 'WorkspaceForm' }}>
        <DetailView
          overrideParams={{ id: id }}
          detailView={true}
          history={this.context.history}
          activeTabId={this.props.formActiveTabId}
        />
      </ViewContextProvider>
    );
  }

  onDeleteClick(value, close) {
    close();
    this.props.dispatch(
      serviceActions.genericAlert('Confirm Delete', 'Do you want to delete this record?', AlertType.Error, {
        alertField: 'deleteRow',
        alertForm: this.props.entityType,
        status: 'CONFIRMATION_REQUIRED',
        confirmationReference: 'deleted',
      })
    );
  }

  _formView(tabEnable, viewController) {
    let data = '';
    let isLoading = false;
    let totalRecords = 1;
    if (this.props.listData) {
      data = this.props.listData.edges;
      isLoading = this.props.loading;
      totalRecords = this.getRowCounts().genericCount;
    }
    const detailView = this.renderForm();

    const formHeight = this.props.embeddedView ? this.state.contentHeight : this.state.contentHeight + 120;

    return (
      <div style={{ width: '100%' }}>
        {this.props.children}
        <div className="react-grid-List" style={{ width: '300px', float: 'left' }}>
          <GridToolbarContainer>
            <GridToolbarWorkspaceSearch />
            {viewController}
          </GridToolbarContainer>
          <DetailDataGrid
            listData={data}
            isLoading={isLoading}
            rowsCount={totalRecords}
            rowHeight={this.props.detailRowHeight || this.listRowHeight}
            columns={this.state.columns}
            entityType={this.props.entityType}
            contentWidth={this.state.contentWidth}
            sbVisible={false}
            contentHeight={this.state.contentHeight + 70}
            onRowClick={this.onRowClick}
            queueRequestFor={this.queueRequestFor}
            loadMoreRows={this.loadMoreRows}
            onGridSort={this.handleGridSort}
            sortColumn={this.sortBy}
            sortDirection={this.sortDir}
            tabIndex={tabEnable ? 1 : -1}
            cellRenderer={this.props.cellRenderer}
            includeHeaderRow={false}
          />
        </div>
        <div
          style={{
            width: `${this.state.contentWidth - 316}px`,
            float: 'left',
            height: `${formHeight}px`,
            overflow: 'hidden',
          }}
        >
          {detailView}
        </div>
      </div>
    );
  }

  _tableView() {
    const { views } = this.props;
    const viewsLoaded = views && views[this.props.viewScreenName] && views[this.props.viewScreenName].viewsLoaded;

    if (!viewsLoaded || !this.props.views?.[this.props.viewScreenName] || !this.props.currentView) {
      return null;
    }

    let data = '';
    let isLoading = this.props.loading;
    let totalRecords = 0;
    if (this.props.listData) {
      data = this.props.listData.edges;
      isLoading = this.props.loading;
      totalRecords = this.getRowCounts().genericCount;
    }

    let gridWidth = undefined;
    if (!this.props.autoWidth) {
      gridWidth = this.props.embeddedView ? this.state.contentWidth - 8 : this.state.contentWidth;
    }

    let listData, rowCount;
    if (this.props.slowWorkspace) {
      //If we've marked this as a slow loading workspace, then when we're re-loading we can blank out the data/rowcount
      //Which causes the 'empty' view to display, which we replace with a spinner for slow workspaces.
      listData = isLoading ? [] : data;
      rowCount = (!isLoading && totalRecords) || 0;
    } else {
      listData = data;
      rowCount = totalRecords || 0;
    }

    if (this.props.useFluentPrototype) {
      const unwrappedList = listData?.map ? listData.map((edge) => edge.node) : [];

      return (
        <div style={{ height: '500px', position: 'relative' }}>
          <FluentList
            items={unwrappedList}
            columns={this.props.selectedColumns}
            enableRowSelect={this.props.workspaceSelection.enableRowSelect}
            infinite={true}
            queueRequestFor={this.queueRequestFor}
            totalRows={this.props.listData?.count || 0}
          />
        </div>
      );
    }

    return (
      <EditableDataGrid
        ref={this.tableViewRef}
        listData={listData}
        isLoading={isLoading}
        rowsCount={rowCount}
        rowHeight={this.gridRowHeight}
        headerRowHeight={this.gridRowHeight}
        columns={this.props.selectedColumns}
        entityType={this.props.entityType}
        contentWidth={gridWidth}
        sbVisible={this.state.sbVisible}
        contentHeight={this.state.contentHeight}
        onRowClick={this.onRowClick}
        onRowDoubleClick={this.onRowDoubleClick}
        onRowRightClick={this.props.onRowRightClick}
        queueRequestFor={this.queueRequestFor}
        onGridSort={this.handleGridSort}
        onColumnReorder={this.handleColumnReorder}
        sortColumn={this.props.workspaceSelection.orderBy}
        sortDirection={this.props.workspaceSelection.sortDirection}
        enableCellAutoFocus={!!this.props.enableCellAutoFocus}
        enableSelectAll={this.props.enableSelectAll}
        enableCellSelect={this.props.enableCellSelect}
        enableCellSelectColumns={this.props.enableCellSelectColumns}
        onCellSelected={this.props.onCellSelected}
        screenName={this.props.viewScreenName}
        tableName={this.state.tableName}
        dispatch={this.props.dispatch}
        enableRowSelect={this.props.workspaceSelection.enableRowSelect}
        rowKey={'id'}
        onRowSelect={this.props.workspaceSelection.onRowSelect}
        onAllRowsSelected={this.props.workspaceSelection.onAllRowsSelected}
        selectedAll={this.props.workspaceSelection.selectedAll}
        dataid={this.state.tableName}
        onRowUpdated={this.props.onRowUpdated}
        emptyRowsView={this.props.emptyRowsView}
        value={this.props.value}
        mergeEditableData={this.props.mergeEditableData}
        mergeEntity={this.props.mergeEntity}
        mergeObject={this.props.mergeObject}
        actionEntities={this.props.actionEntities}
        expandableRows={this.props.expandableRows}
        selectedRowIds={this.props.workspaceSelection.selectedRowIds}
        miniGrid={this.props.miniGrid}
        rowRenderer={this.props.rowRenderer}
        renderActionButtons={getWorkspaceContextButtons({
          enableRowSelect: this.props.workspaceSelection.enableRowSelect,
          permittedOperations: this.props.permittedOperations,
          noEdit: this.props.noEdit,
          formUrl: this.props.formUrl ?? this.props.entity?.formUrl,
          onEditClick: this.props.onEditClick,
          formIdOverride: this.props.formIdOverride,
          duplicate: this.props.duplicate,
          duplicateData: this.duplicateData,
          handleActionSort: this.handleActionSort,
          renderActionButtons: this.props.renderActionButtons,
          columnOverrides: this.props.columnOverrides,
        })}
        contextKey={this.props.contextKey || this.props.dataid}
        gridType={'workspace'}
        showSpinnerWhileLoading={this.props.slowWorkspace}
        ignoreContainerWidth={this.props.ignoreContainerWidth}
        checkboxSide={this.props.checkboxSide}
        className={this.props.className}
        defaultEditable={false}
      />
    );
  }

  updateTableNameIfUrlHistoryUpdated(prevProps) {
    const nextProps = this.props;
    if (
      nextProps.location &&
      prevProps.location &&
      nextProps.location.key !== prevProps.location.key &&
      this.state.tableName === 'form'
    ) {
      this.setState({ tableName: 'grid' });
    }
  }

  reloadData = () => {
    return this.props.loadMoreRows(0, true).then((result) => {
      if (!this.props.preventSelectionResetOnFilterChange) {
        this.props.workspaceSelection.onRowSelect([], []);
      }

      return result;
    });
  };

  render() {
    let contentView = null;
    const tabEnable = this.props.tabIndex === -1 ? false : this.props.focusArea === 'Work';

    const viewSwitcher = this.props.disableViewController ? null : this.renderViewController(tabEnable, false);

    switch (this.state.tableName) {
      case 'grid':
        contentView = this._tableView();
        break;
      case 'form':
        contentView = this.props.detailView ? this._formView(tabEnable, viewSwitcher) : this._tableView();
        break;
    }

    //Optional - A wrapper of WorkspaceActionButtons may be passed to allow adding in additional buttons/props (E.g. For Workspace Actions with Hooks).
    const WrapperWorkspaceActionButtons = this.props.workspaceActionButtonsHOC || WorkspaceActionButtons;
    //Ignore the warning on ViewContext, do *not* provide viewScreenName or nested forms will show unexpected shimmer rows
    const workspaceActionButtons = (
      <WrapperWorkspaceActionButtons
        workspaceProps={this.props}
        actionButtons={this.props.actionButtons}
        handleActionOption={this.handleActionOption}
        enableRowSelect={this.props.workspaceSelection.enableRowSelect}
        actionEntities={this.props.actionEntities}
        enableDisableRowSelect={this.props.workspaceSelection.enableDisableRowSelect}
        styles={this.styles}
        noReport={this.props.noReport}
      />
    );

    const viewController = this.props.disableViewController ? null : this.renderViewController(tabEnable);
    const viewToolbar = this.props.disableViewToolbar ? null : this.renderViewToolbar(tabEnable, viewController);

    const addButton = this.renderAddOptions();

    const { module, submodule, subtitle, modtitle } = getModuleName(this.props.location);
    const { embeddedView, modalContentLink, title, infoBannerText, infoBannerAction, customHeader } = this.props;

    let infoBanner = null;
    let infoBannerActionButton = null;

    if (infoBannerAction) {
      infoBannerActionButton = (
        <InfoBannerAction>
          <button
            style={{ minWidth: `${150}px` }}
            className={this.styles.btnInfo}
            onClick={infoBannerAction.onClick}
            tabIndex={tabEnable ? 1 : -1}
          >
            {infoBannerAction.label}
          </button>
        </InfoBannerAction>
      );
    }

    if (infoBannerText) {
      infoBanner = (
        <InfoBannerOuter>
          <InfoBanner>
            {infoBannerText}
            {infoBannerActionButton}
          </InfoBanner>
        </InfoBannerOuter>
      );
    }

    const totalsView = (
      <TotalsView
        listData={this.props.listData}
        embeddedView={this.props.embeddedView}
        additionalCards={this.props.additionalCards}
        entity={this.props.entity}
        contentWidth={this.state.contentWidth}
      />
    );
    const workspaceOverviewBar = (
      <WorkspaceOverviewBar listData={this.props.listData} {...this.props.workspaceOverviewBarProps} />
    );

    if (this.props.miniGrid) {
      return (
        <div className={this.styles.contentContainer} ref={this.workspaceContainerRef}>
          {contentView}
        </div>
      );
    }

    const closeButton = this.props.onClose ? (
      <SecondaryButton onClick={this.props.onClose} tabIndex={tabEnable ? 1 : -1}>
        Close
      </SecondaryButton>
    ) : null;

    const header =
      this.props.includeHeader || !this.props.embeddedView ? (
        <div
          className={this.props.hookStyles?.headerContainer ?? this.styles.hdrContainer}
          style={this.props.embeddedView ? { paddingLeft: '17px', paddingTop: '3px', paddingRight: '9px' } : undefined}
        >
          <div className={this.props.hookStyles?.headerTitle ?? this.styles.hdrTitle}>
            <h2 className={this.props.hookStyles?.headerHeading ?? this.styles.hdrHeading}>
              <div>
                {title}
                {this.props.beta ? <BetaTag type="workspace" /> : null}
              </div>
            </h2>
            {this.props.secondaryTitle ? <h6>{this.props.secondaryTitle}</h6> : null}
          </div>
          <div
            className={this.props.hookStyles?.headerAction ?? this.styles.hdrAction}
            style={{ width: this.props.includeHeader ? '11%' : '30%' }}
          >
            {closeButton}
            {viewController}
            <CloseModalActionButton />
            {!this.props.disableActionButtons && (!this.props.embeddedView || this.props.includeHeader)
              ? workspaceActionButtons
              : null}
            {addButton}
          </div>
        </div>
      ) : null;

    //'-webkit-box-pack': 'start',
    //'-webkit-box-pack': 'end',

    const { rowCount, selectedRowCount } = this.getRowCounts();

    return (
      <ViewContextProvider
        value={{
          currentView: this.props.currentView,
          availableColumns: this.props.availableColumns,
          viewScreenName: this.props.viewScreenName,
          views: this.props.views,
        }}
      >
        <WorkspaceContext.Provider
          value={{
            title: this.props.title,
            reloadData: this.reloadData,
            selectionState: this.props.workspaceSelection.getSelectionState(),
            workspaceFilters: this.props.workspaceFilters,
            currentView: this.props.currentView, //TODO - Can we remove this? (It's provided by the ViewContext.Provider 10 lines or so above...)
            entity: this.props.entity || { type: this.props.entityType },
            screenReference: this.props.screenReference,
            onRowSelect: this.props.workspaceSelection.onRowSelect,
            workspaceSelection: this.props.workspaceSelection,
            embeddedView: embeddedView,
            rowCount,
            selectedRowCount,
            availableFiltersFilter: this.props.availableFiltersFilter,
            availableColumnsFilter: this.props.availableColumnsFilter,
            workspaceActionButtons: workspaceActionButtons,
            autofocusSearch: this.props.autofocusSearch,
          }}
        >
          {embeddedView ? (
            <div className={this.styles.contentContainer} ref={this.workspaceContainerRef}>
              <div className={this.styles.contentEmbedded}>
                <FilterListsView contentWidth={this.state.contentWidth} />
                {workspaceOverviewBar}
                {header}
                {customHeader}
                {this.props.children}
                {totalsView}
                {viewToolbar}
                {modalContentLink && addButton ? (
                  <ModalContentLink
                    key="embeddedModal"
                    content={modalContentLink}
                    enabled={
                      !this.props.noAddOption &&
                      this.props.formUrl &&
                      this.props.permittedOperations & ALLOW_ADD &&
                      checkOperation(this.props, Operations.Create)
                    }
                    onModalSelect={this.onModalSelect}
                    getFormUrl={this.props.getFormUrl}
                    embeddedView={this.props.embeddedView}
                  />
                ) : null}
                {contentView}
              </div>
            </div>
          ) : (
            <div className={this.props.hookStyles?.container ?? this.styles.container} ref={this.workspaceContainerRef}>
              {this.state.tableName !== 'form' ? (
                <>
                  <div className={this.props.hookStyles?.headerBreadcrumb ?? this.styles.hdrBreadcrumb}>
                    {this.props.customBreadcrumb ? (
                      generateCustomBreadcrumb(this.props.customBreadcrumb, this.props.history, true)
                    ) : (
                      <div>
                        <i onClick={() => this.backToMenu('/')} className={`${this.styles.pageHeaderIcon} icon`}>
                          home
                        </i>
                        <span onClick={() => this.backToMenu(`/menu/${module}`)}>{modtitle}</span>
                        {submodule && !this.props.excludeSubModuleFromBreadcrumb ? (
                          <span
                            onClick={() => this.backToMenu(`/menu/${module}/${submodule}`)}
                            className={this.styles.pageHeaderNav}
                          >
                            {' '}
                            / {subtitle}
                          </span>
                        ) : null}
                      </div>
                    )}
                  </div>
                  {header}
                  {customHeader}
                  <FilterListsView contentWidth={this.state.contentWidth} />
                  {totalsView}
                  {workspaceOverviewBar}
                  {this.props.children}
                </>
              ) : null}
              <div className={this.styles.contentContainer}>
                {infoBanner}
                <div className={this.styles.content} style={{ width: `${this.state.contentWidth}px` }}>
                  {this.state.tableName !== 'form' ? viewToolbar : null}
                  {contentView}
                </div>
              </div>
            </div>
          )}
        </WorkspaceContext.Provider>
      </ViewContextProvider>
    );
  }
}

Workspace.propTypes = {
  actionEntities: PropTypes.any,
  addButtons: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      url: PropTypes.string,
    })
  ),
  addButtonLabel: PropTypes.string,
  app: PropTypes.shape({
    appMenuHidden: PropTypes.bool,
    appSidebarHidden: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
  }),
  autoFocus: PropTypes.bool,
  backButtonRequired: PropTypes.bool,
  onBack: PropTypes.func,
  card: PropTypes.arrayOf(PropTypes.string),
  cellRenderer: PropTypes.func,
  columnOverrides: PropTypes.object,
  columns: PropTypes.arrayOf(PropTypes.string),
  contentWidth: PropTypes.number,
  contentHeight: PropTypes.number,
  customColumns: PropTypes.object,
  detailParams: PropTypes.object,
  detailView: PropTypes.elementType,
  dispatch: PropTypes.func,
  embeddedView: PropTypes.bool,
  emptyRowsView: PropTypes.func,
  enableSelectAll: PropTypes.bool,
  enableCellSelect: PropTypes.bool,
  enableCellSelectColumns: PropTypes.array,
  entity: PropTypes.shape({
    type: PropTypes.string,
    workspaceUrl: PropTypes.string,
    actions: PropTypes.arrayOf(
      PropTypes.shape({
        call: PropTypes.func,
        condition: PropTypes.func,
        description: PropTypes.any,
        dispatch: PropTypes.func,
      })
    ),
  }),
  entityType: PropTypes.string,
  fixedFilters: PropTypes.array,
  focusArea: PropTypes.string,
  forceAutoFocus: PropTypes.bool,
  formUrl: PropTypes.string,
  getFixedFilters: PropTypes.func,
  hideColumnSelector: PropTypes.bool,
  hideViewController: PropTypes.bool,
  initialSortColumn: PropTypes.string,
  initialSortDir: PropTypes.string,
  lookupView: PropTypes.bool,
  messageDefinition: PropTypes.object, // Any additional data required other than selected columns
  mergeEditableData: PropTypes.bool,
  mergeEntity: PropTypes.string,
  mode: PropTypes.string, //'full'
  onRowClick: PropTypes.func,
  onRowSelect: PropTypes.func,
  onRowUpdated: PropTypes.func,
  pageSize: PropTypes.number,
  permittedOperations: PropTypes.number,
  sidebar: PropTypes.arrayOf(PropTypes.string),
  tabIndex: PropTypes.number,
  title: PropTypes.string,
  viewScreenName: PropTypes.string,
  views: PropTypes.object,
  includeGenericSearch: PropTypes.bool,
  availableFiltersFilter: PropTypes.func,
  availableColumnsFilter: PropTypes.func,
  noAddOption: PropTypes.bool,
  additionalCards: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.number })),
  formActiveTabId: PropTypes.string,
  childrenHeight: PropTypes.number,
  detailRowHeight: PropTypes.number,
  customToolbarAction: PropTypes.node,
  preventSelectionResetOnFilterChange: PropTypes.bool,
  autofocusSearch: PropTypes.bool,
};

Workspace.contextType = AppContext;

Workspace.defaultProps = {
  pageSize: 30,
  pageText: 'Items',
  title: 'Data',
  id: 'ws-grid',
  initialSortDir: 'ASC',
  className: '',
  columns: [],
  card: [],
  form: [],
  sidebar: [],
  contentWidth: null,
  contentHeight: null,
  onRowClick: function () {},
  onRowDoubleClick: function () {},
  reportParams: {},
  mode: 'full',
  actionButtons: [],
  actionEntities: [],
  addButtons: [],
  addButtonLabel: 'Add',
  permittedOperations: ALLOW_VIEW | ALLOW_ADD | ALLOW_EDIT | ALLOW_DELETE,
  embeddedView: false,
  readOnlyStatus: null,
  tabIndex: null,
  backButtonRequired: false,
  onBack: () => {},
  customColumns: {},
  enableSelectAll: true,
  enableCellSelect: false,
  enableCellSelectColumns: [],
  onRowUpdated: null,
  hideColumnSelector: false,
  hideViewController: false,
  emptyRowsView: null,
  mergeEditableData: false,
  mergeEntity: null,
  excludeSubModuleFromBreadcrumb: false,
  enableRowSelect: false,
  noReport: false, //Do not include the dynamic report option on this workspace.
  customHeader: null, //@Deprecated - Use children on the workspace instead TODO - Replace existing uses with children approach.
  includeGenericSearch: true,
  disableViewController: false,
  disableActionButtons: false,
  disableViewToolbar: false,
  childrenHeight: 0,
  customToolbarAction: null,
  preventSelectionResetOnFilterChange: false,
};

const useWorkspaceSelectedColumns = ({
  availableColumns,
  columnOverrider,
  columnOverrides,
  noEdit,
  permittedOperations,
  formUrl,
  formIdOverride,
  entity,
  entityType,
  readOnlyStatus,
  editInNewTab,
  miniGrid,
  formActiveTabId,
  contextOptions,
  allowView,
  additionalColumns,
  screenReference,
  currentView,
  getFormUrl,
  onEditClick,
  resetTriggered,
  viewScreenName,
  onResetDispatched,
}) => {
  const fullUserDetails = useSelector((store) => store.app.fullUserDetails);

  const dispatch = useDispatch();
  const [columnResetTriggered, setColumnResetTriggered] = useState(false);
  const [originalColumns, setOriginalColumns] = useState([]);

  useEffect(() => {
    setColumnResetTriggered(resetTriggered);
  }, [resetTriggered]);

  const getSelectedColumns = useCallback(
    (view) => {
      let columnDefinition, tableColumn;
      return view.columns.map((column) => {
        columnDefinition = availableColumns.find((availableColumn) => {
          return availableColumn.fieldName === column.fieldName;
        });
        tableColumn = Object.assign({}, columnDefinition, {
          width: column.width,
          position: column.position,
        });
        tableColumn.name = column.title;
        tableColumn.key = column.fieldName;
        if (column.sortable !== undefined) {
          tableColumn.sortable = column.sortable;
        }
        // Add a thousand separator to decimal values
        if (tableColumn.type === 'BigDecimal') {
          tableColumn.formatter = 'numeric';
          tableColumn = setIn(tableColumn, `options.format.thousandSeparator`, true);
        }
        return columnOverrider ? columnOverrider(tableColumn) : tableColumn;
      });
    },
    [availableColumns, columnOverrider]
  );

  const getContextOptionsDefault = useCallback(() => {
    const contextOptions = [];
    if (allowView) {
      contextOptions.push({
        label: 'Edit',
        onClick: onEditClick,
      });
    }
    Array.prototype.push.apply(contextOptions, contextOptions);
    return contextOptions;
  }, [allowView, onEditClick]);

  const sidebarContext = useContext(SidebarContext);

  return useMemo(() => {
    if (!currentView) return null;
    const selectedColumns = getSelectedColumns(currentView);

    // Add on edit icon column if not present
    const idColumnIndex = selectedColumns.findIndex((column) => {
      return column.key === 'id' || column.key === formIdOverride;
    });
    if (!noEdit) {
      let idColumn = null;
      if (columnOverrides && columnOverrides.id) {
        idColumn = Object.assign({}, columnOverrides.id);
        idColumn.key = 'id';
      } else if (formUrl) {
        const permitted = checkOperation({ screenReference, entity, app: { fullUserDetails } }, Operations.Update);
        const rowActionTitle = permitted && permittedOperations & ALLOW_EDIT ? 'Edit' : 'View';

        idColumn = {
          key: formIdOverride || 'id',
          name: '',
          editable: false,
          resizable: false,
          sortable: false,
          width: 45,
          formatter: Formatters.LinkFormatter,
          options: {
            url: getFormUrl(),
            formUrlFromFunction: getFormUrl,
            entity: { type: entityType },
            title: rowActionTitle,
            readOnlyStatus: readOnlyStatus,
            editInNewTab: editInNewTab,
            miniGrid: miniGrid,
            modal: true,
            activeTabId: formActiveTabId,
            onClick: (value, rowData, event) => {
              // If the current sidebar context is within a modal and this is a supported type, open the sidebar instead of navigating.
              if (sidebarContext.modal && entityType === 'Product' && value) {
                switch (entityType) {
                  case 'Product':
                    event.preventDefault();
                    sidebarContext.openSidebar(SidebarType.ProductForm, rowData.id);
                    return;
                  case 'Supplier':
                    event.preventDefault();
                    sidebarContext.openSidebar(SidebarType.SupplierForm, rowData.id);
                    return;
                  case 'Customer':
                    event.preventDefault();
                    sidebarContext.openSidebar(SidebarType.CustomerForm, rowData.id);
                    return;
                }
              }
            },
          },
          draggable: false,
        };
      }
      if (idColumn) {
        if (contextOptions) {
          idColumn.formatter = Formatters.ContextOptionsFormatter;
          idColumn.options = getContextOptionsDefault();
        }

        if (idColumnIndex >= 0) {
          selectedColumns.splice(idColumnIndex, 1, idColumn);
        } else {
          selectedColumns.push(idColumn);
        }
      }
    }
    if (additionalColumns) {
      selectedColumns.push(...additionalColumns);
    }

    const newColumns = [];
    selectedColumns?.forEach((column) => {
      const isNewColumn = !originalColumns.some((origColumn) => origColumn.key === column.key);
      if (isNewColumn) {
        newColumns.push(column);
      }
    });

    if (newColumns.length > 0 || selectedColumns.length !== originalColumns.length) {
      setOriginalColumns(selectedColumns);
    }

    if (columnResetTriggered) {
      onResetDispatched();

      const updatedColumns = originalColumns
        .filter((column) => column.key !== 'id' && column.key !== formIdOverride)
        .map((column) => {
          const selectedColumn = selectedColumns.find((selected) => selected.key === column.key);
          return selectedColumn ? { ...column, position: selectedColumn.position } : column;
        });

      dispatch(viewActions.updateColumns(viewScreenName, updatedColumns));

      dispatch(
        serviceActions.genericAlert('Columns reset', 'Column widths have been reset to default', AlertType.Info)
      );
    }

    return selectedColumns;
  }, [
    additionalColumns,
    columnOverrides,
    contextOptions,
    currentView,
    editInNewTab,
    entity,
    entityType,
    formActiveTabId,
    formIdOverride,
    formUrl,
    fullUserDetails,
    getContextOptionsDefault,
    getFormUrl,
    getSelectedColumns,
    miniGrid,
    noEdit,
    permittedOperations,
    readOnlyStatus,
    screenReference,
    originalColumns,
    columnResetTriggered,
  ]);
};

const useStyles = makeStyles({
  container: {
    height: '100%',
    position: 'relative',
    backgroundColor: p4Tokens.colorHeaderBackground,
    padding: '16px 20px',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '12px',
    backgroundColor: p4Tokens.colorHeaderBackground,
  },
  headerBreadcrumb: {
    fontSize: '13px',
    color: p4Tokens.colorBrandMenuForeground5,
    padding: '0px',
    margin: 0,
    lineHeight: 1.1,
    fontWeight: 400,
    cursor: 'pointer',
    backgroundColor: p4Tokens.colorHeaderBackground,
  },
  headerTitle: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  headerHeading: {
    fontSize: '22px',
    fontWeight: '500',
    color: p4Tokens.fieldLabel,
    padding: '4px 16px 4px 0px',
    margin: 0,
  },
  headerAction: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '30%',
  },
});

//Functional wrapper for slow migration.
const WrappedWorkspace = forwardRef((props, ref) => {
  const isLoggedIn = useSelector((store) => store.app.isLoggedIn);
  const appMenuHidden = useSelector((store) => store.app.appMenuHidden);
  const user = useSelector((store) => store.app.user);
  const appSidebarHidden = useSelector((store) => store.app.appSidebarHidden);
  const fullUserDetails = useSelector((store) => store.app.fullUserDetails);
  const app = useMemo(
    () => ({ isLoggedIn, appMenuHidden, user, appSidebarHidden, fullUserDetails }),
    [appMenuHidden, appSidebarHidden, fullUserDetails, isLoggedIn, user]
  );

  const [resetTriggered, setResetTriggered] = useState(false);
  const connectedForm = useContext(ConnectedFormContext);
  const dispatch = useDispatch();
  const history = useHistory();
  const company = useCompany();

  const styles = useStyles();

  const {
    entity,
    entityType: entityTypeFromProps,
    formActiveTabId,
    formUrl: formUrlFromProps,
    title: titleFromProps,
    readOnlyStatus,
    availableColumns,
    columnOverrider,
    columnOverrides,
    noEdit,
    permittedOperations,
    formIdOverride,
    editInNewTab,
    miniGrid,
    contextOptions,
    allowView,
    additionalColumns,
    screenReference,
    loadMoreRows,
    viewScreenName: viewScreenNameFromProps,
    location,
    dynamicDefaultColumns,
    columns,
    currentView,
    workspaceSelection,
  } = props;

  //Allow these fields to be skipped when providing props because we can get them directly off 'entity'.
  const title = titleFromProps ?? entity?.title;
  const formUrl = formUrlFromProps ?? entity?.formUrl;
  const entityType = entityTypeFromProps ?? entity?.type;

  //Register this workspaces' 'reload' function with any parent forms, so that if the form data is updated the workspace is refreshed.
  useEffect(() => {
    const reload = () => loadMoreRows(0, true);

    if (connectedForm?.workspaceReloadRef?.current) {
      connectedForm.workspaceReloadRef.current.push(reload);

      return () => {
        const reloadIndex = connectedForm.workspaceReloadRef.current.findIndex((x) => x === reload);
        if (reloadIndex >= 0) {
          connectedForm.workspaceReloadRef.current.splice(reloadIndex, 1);
        }
      };
    }
    // didMount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const screenName = viewScreenNameFromProps || location.pathname.slice(1);
  const viewScreenName = screenName.endsWith('/') ? screenName.slice(0, -1) : screenName;

  useEffect(() => {
    //Effect for regenerating the default view when the provided column definitions are updated for some given reason.
    //Locked behind a prop, because this may not be desirable behaviour for other workspaces.
    if (dynamicDefaultColumns) {
      const defaultFilter = location?.state?.genericFilter;
      const defaultViewConfig = createDefaultViewConfig(props, defaultFilter);

      dispatch(
        viewActions.resetDefaultView({
          screenName: viewScreenName,
          defaultViewConfig: defaultViewConfig,
        })
      );
    }
  }, [columns?.length]);

  const getFormUrl = useCallback(
    (rowData) => {
      if (!rowData) return formUrl;

      const formUrlFromFunction = entity?.formUrlFromFunction;
      if (formUrlFromFunction) {
        return formUrlFromFunction(rowData);
      }
      return formUrl;
    },
    [entity?.formUrlFromFunction, formUrl]
  );
  const onEditClick = useCallback(
    (value, close, openInNewTab) => {
      if (close) close();
      const url = getFormUrl() + value; //TODO - Get Row Data somehow???
      const entity = entityType;
      const modal = true;
      const readOnly = readOnlyStatus;
      if (openInNewTab) {
        window.open(window.location.origin + url, '_blank');
        return;
      }

      history.push({
        pathname: url,
        state: {
          modal,
          entity,
          popover: false,
          readOnly,
          previousLocation: history.location?.pathname || 'back',
          activeTabId: formActiveTabId,
        },
      });
    },
    [entityType, formActiveTabId, getFormUrl, history, readOnlyStatus]
  );

  const selectedColumns = useWorkspaceSelectedColumns({
    availableColumns: availableColumns,
    columnOverrider: columnOverrider,
    columnOverrides: columnOverrides,
    noEdit: noEdit,
    permittedOperations: permittedOperations,
    formUrl: formUrl,
    formIdOverride: formIdOverride,
    entity: entity,
    entityType: entityType,
    readOnlyStatus: readOnlyStatus,
    editInNewTab: editInNewTab,
    miniGrid: miniGrid,
    formActiveTabId: formActiveTabId,
    contextOptions: contextOptions,
    allowView: allowView,
    additionalColumns: additionalColumns,
    screenReference: screenReference,
    currentView: currentView,
    getFormUrl,
    onEditClick,
    resetTriggered: resetTriggered,
    viewScreenName: viewScreenName,
    onResetDispatched: () => setResetTriggered(false),
  });

  //Fetch sidebar context for use in Workspace component - If/when Workspace finally becomes a Function Component that should be moved into it for direct access.
  const sidebarContext = useContext(SidebarContext);

  /**
   * If selection has already been configured
   */
  const WorkspaceComponent = workspaceSelection ? Workspace : WorkspaceWithSelection;

  return (
    <WorkspaceComponent
      {...props}
      app={app}
      ref={ref}
      viewScreenName={viewScreenName}
      dispatch={dispatch}
      company={company}
      selectedColumns={selectedColumns}
      currentView={currentView}
      getFormUrl={getFormUrl}
      onEditClick={onEditClick}
      onResetClick={() => {
        setResetTriggered(true);
      }}
      title={title}
      sidebarContext={sidebarContext}
      hookStyles={styles} //hookStyles a temporary field - Need to update Workspace to a FC and call the hook directly.
    />
  );
});

const WorkspaceWithSelection = forwardRef((props, ref) => {
  const workspaceSelection = useWorkspaceSelection({
    enableRowSelect: props.enableRowSelect ?? true, // enable row selection by default
    listData: props.listData,
    actionEntities: props.actionEntities,
    viewScreenName: props.viewScreenName,
    match: props.match,
    workspaceFilters: props.workspaceFilters,
    initialSelectedRows: props.selectedRowIds ?? [],
    initialSelectedRowIds: props.selectedRowIds ?? [],
    onRowSelect: props.onRowSelect,
    preventSelectionResetOnFilterChange: props.preventSelectionResetOnFilterChange,
    initialSortColumn: props.initialSortColumn,
    initialSortDirection: props.initialSortDir,
    currentView: props.currentView, // Just used for orderBy/orderDirection - To be restructured in release/13
  });

  return <Workspace ref={ref} {...props} workspaceSelection={workspaceSelection} />;
});

WrappedWorkspace.displayName = 'Workspace';

export default WrappedWorkspace;
